import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Popover,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { Info as InfoIcon, Edit as EditIcon, ViewQuilt as LayoutIcon, Visibility as PreviewIcon } from '@mui/icons-material';
import EmailBuilder, { EmailBuilderValue } from './EmailBuilder/EmailBuilder';
import { apiClient } from '../../../utils/ApiClient';
import { useSelector } from 'react-redux';
import { RootState } from '../../../state/store';
import { generateThumbnailFromHtml } from '../../../utils/ThumbnailGenerator';

interface EmailTemplateDialogProps {
  open: boolean;
  onClose: () => void;
  template: any;
  dataSets: any[];
  availableColumns: string[];
  loadingColumns: boolean;
  getDisplayCollectionName: (name?: string) => string;
  companyId: string;
  onSuccess: () => void;
  onDataSetChange?: (dataSetId: string) => void;
  apiEndpointPrefix?: string;
}

export const EmailTemplateDialog: React.FC<EmailTemplateDialogProps> = ({
  open,
  onClose,
  template,
  dataSets,
  availableColumns,
  loadingColumns,
  getDisplayCollectionName,
  companyId,
  onSuccess,
  onDataSetChange,
  apiEndpointPrefix
}) => {
  const endpointBase = apiEndpointPrefix || '/iu/contenttemplate';
  const user = useSelector((state: RootState) => state.authentication.user);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  type MobileSection = 'fields' | 'layout' | 'preview';
  const [mobileActiveSection, setMobileActiveSection] = useState<MobileSection>('layout');

  const [currentTemplate, setCurrentTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailBuilderValue, setEmailBuilderValue] = useState<EmailBuilderValue | null>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
    }
  }, [open]);
  const [variablesAnchorEl, setVariablesAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleVariablesPopoverOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setVariablesAnchorEl(event.currentTarget);
  };

  const handleVariablesPopoverClose = () => {
    setVariablesAnchorEl(null);
  };

  const variablesPopoverOpen = Boolean(variablesAnchorEl);

  // Helper to extract display template name without IUT- prefix and company ID
  const getDisplayTemplateName = (fullTemplateName?: string) => {
    if (!fullTemplateName) return '';
    const parts = fullTemplateName.split('-');
    if (parts.length >= 3 && parts[0] === 'IUT' && parts[1] === companyId) {
      return parts.slice(2).join('-');
    }
    return fullTemplateName;
  };

  // Initialize template data when dialog opens
  useEffect(() => {
    if (open && template) {
      setCurrentTemplate({
        ...template,
        templateName: template.templateName || '',
        templateType: template.templateType || 'EMAIL',
        accessLevel: template.accessLevel || 'public',
        emailSubject: template.emailSubject || '',
        collectionId: template.collectionId || '',
        templateContent: template.templateContent || '',
        emailBuilderConfig: template.emailBuilderConfig || null
      });
      setError(null);
    }
  }, [open, template, dataSets]);

  const handleTextFieldChange = (key: string, value: any) => {
    setCurrentTemplate((prev: any) => prev ? { ...prev, [key]: value } : null);
  };

  const handleEmailBuilderChange = (value: EmailBuilderValue) => {
    setEmailBuilderValue(value);
    // Persist the raw builder config in state to save to server
    handleTextFieldChange('emailBuilderConfig', value);
  };

  // Removed naive HTML generator; rely on EmailBuilder export

  const validateForm = (): boolean => {
    if (!currentTemplate?.templateName?.trim()) {
      setError('Template Name is required');
      return false;
    }
    if (!currentTemplate?.emailSubject?.trim()) {
      setError('Email Subject is required');
      return false;
    }
    if (!currentTemplate?.collectionId) {
      setError('Data Set is required');
      return false;
    }
    setError(null);
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const payload: any = {
        templateName: currentTemplate.templateName,
        templateType: currentTemplate.templateType,
        accessLevel: currentTemplate.accessLevel,
        emailSubject: currentTemplate.emailSubject,
        collectionId: currentTemplate.collectionId,
        templateContent: currentTemplate.templateContent || '',
        emailBuilderConfig: currentTemplate.emailBuilderConfig || emailBuilderValue || null,
        mediaAttachments: template.mediaAttachments || [],
        category: currentTemplate.category || undefined,
        keywords: currentTemplate.keywords?.length ? currentTemplate.keywords : undefined,
      };
      if (!apiEndpointPrefix) {
        payload.companyId = user.companyId;
      }
      try {
        // Headless thumbnail generation. The `ThumbnailGenerator` inlines any
        // remote (cross-origin) `<img>` references — e.g. published Asset
        // Explorer URLs served from the S3 CDN — through the same-origin
        // `/ae/assets/published-proxy` endpoint before invoking html2canvas,
        // so the resulting PNG always contains the real asset bitmaps without
        // tripping CORS / canvas-taint protections.
        const thumbnailDataUrl = await generateThumbnailFromHtml(payload.templateContent);
        if (thumbnailDataUrl) {
          payload.thumbnail = { data: thumbnailDataUrl, mimeType: 'image/png' };
        }
      } catch (thumbErr) {
        console.error('Thumbnail generation failed (non-blocking):', thumbErr);
      }
      const result = await apiClient.put(`${endpointBase}/${template._id}`, payload);

      console.log('Email template updated successfully:', result);
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while updating the template');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      setCurrentTemplate(null);
      setEmailBuilderValue(null);
      onClose();
    }
  };

  if (!currentTemplate) {
    return (
      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullScreen
      sx={{
        '& .MuiDialog-paper': {
          margin: 0,
          maxWidth: '100vw',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          ...(isMobile ? {
            height: '100dvh',
            maxHeight: '100dvh',
            paddingTop: 'max(env(safe-area-inset-top, 0px), 48px)',
          } : {
            height: '100vh',
            maxHeight: '100vh',
          }),
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1, px: 2, minHeight: 'auto' }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
          Email Builder{!isMobile ? ` : ${getDisplayTemplateName(currentTemplate?.templateName)}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, flexShrink: 0 }}>
          <Button 
            onClick={handleClose} 
            variant="outlined"
            color="secondary"
            size="small"
            disabled={loading}
            sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', minHeight: 28 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="success"
            size="small"
            disabled={loading}
            sx={{ py: 0.25, px: 1.5, fontSize: '0.75rem', minHeight: 28 }}
          >
            {loading ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </DialogTitle>

      {/* Mobile-only section toolbar */}
      {isMobile && (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: (t) => t.palette.mode === 'dark' ? 'rgba(30, 35, 60, 0.7)' : 'rgba(240, 242, 245, 0.95)',
          flexShrink: 0,
        }}>
          <IconButton
            size="small"
            onClick={() => setMobileActiveSection('fields')}
            sx={{
              borderRadius: 1,
              border: '2px solid',
              borderColor: mobileActiveSection === 'fields' ? 'secondary.main' : 'divider',
              backgroundColor: mobileActiveSection === 'fields' ? 'secondary.main' : 'transparent',
              color: mobileActiveSection === 'fields' ? 'primary.main' : 'text.secondary',
              px: 1.25,
              '&:hover': { borderColor: 'secondary.main', backgroundColor: mobileActiveSection === 'fields' ? 'secondary.dark' : 'action.hover' },
            }}
          >
            <EditIcon sx={{ fontSize: 18, mr: 0.5 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase' }}>Fields</Typography>
          </IconButton>
          <Box sx={{ display: 'flex', gap: 0.75 }}>
            <IconButton
              size="small"
              onClick={() => setMobileActiveSection('layout')}
              sx={{
                borderRadius: 1,
                border: '2px solid',
                borderColor: mobileActiveSection === 'layout' ? 'secondary.main' : 'divider',
                backgroundColor: mobileActiveSection === 'layout' ? 'secondary.main' : 'transparent',
                color: mobileActiveSection === 'layout' ? 'primary.main' : 'text.secondary',
                px: 1.25,
                '&:hover': { borderColor: 'secondary.main', backgroundColor: mobileActiveSection === 'layout' ? 'secondary.dark' : 'action.hover' },
              }}
            >
              <LayoutIcon sx={{ fontSize: 18, mr: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase' }}>Layout</Typography>
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setMobileActiveSection('preview')}
              sx={{
                borderRadius: 1,
                border: '2px solid',
                borderColor: mobileActiveSection === 'preview' ? 'secondary.main' : 'divider',
                backgroundColor: mobileActiveSection === 'preview' ? 'secondary.main' : 'transparent',
                color: mobileActiveSection === 'preview' ? 'primary.main' : 'text.secondary',
                px: 1.25,
                '&:hover': { borderColor: 'secondary.main', backgroundColor: mobileActiveSection === 'preview' ? 'secondary.dark' : 'action.hover' },
              }}
            >
              <PreviewIcon sx={{ fontSize: 18, mr: 0.5 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.7rem', textTransform: 'uppercase' }}>Preview</Typography>
            </IconButton>
          </Box>
        </Box>
      )}
      
      <DialogContent sx={{ 
        flex: 1,
        minHeight: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        px: isMobile ? 1 : undefined,
      }}>
        <Box sx={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1, flexShrink: 0 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1.5 : 2, mt: isMobile ? 1 : 2, height: '100%', minHeight: 0 }}>
            {/* Section 1: Fields (DataSet, Access Level, Subject) – always on desktop, conditional on mobile */}
            {(!isMobile || mobileActiveSection === 'fields') && (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <FormControl fullWidth>
                      <Autocomplete
                        size="small"
                        options={dataSets}
                        getOptionLabel={(option) => getDisplayCollectionName(option?.collectionName)}
                        value={dataSets.find((ds) => ds._id === currentTemplate?.collectionId) || null}
                        onChange={(_, newValue) => {
                          setCurrentTemplate((prev: any) => (prev ? { ...prev, collectionId: newValue?._id } : null));
                          if (onDataSetChange && newValue?._id) {
                            onDataSetChange(newValue._id);
                          }
                        }}
                        renderInput={(params) => <TextField {...params} label="Select DataSet" />}
                      />
                    </FormControl>
                    {currentTemplate?.collectionId && (
                      <IconButton
                        onClick={handleVariablesPopoverOpen}
                        size="small"
                        sx={{
                          mt: 0.5,
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            borderColor: 'primary.main',
                          }
                        }}
                        title="View Available Variables"
                      >
                        <InfoIcon fontSize="small" color="info" />
                      </IconButton>
                    )}
                  </Box>
                  <FormControl fullWidth>
                    <InputLabel>Access Level</InputLabel>
                    <Select
                      size="small"
                      label="Access Level"
                      value={currentTemplate?.accessLevel || ''}
                      onChange={(e) => setCurrentTemplate((prev: any) => prev ? { ...prev, accessLevel: e.target.value } : null)}
                    >
                      <MenuItem value="public">Public</MenuItem>
                      <MenuItem value="private">Private</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                  <TextField
                    select
                    size="small"
                    label="Category"
                    value={currentTemplate?.category || ''}
                    onChange={(e) => setCurrentTemplate((prev: any) => prev ? { ...prev, category: e.target.value || undefined } : null)}
                    fullWidth
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (v: unknown) => {
                        const val = v as string;
                        if (!val) return <Typography variant="body2" color="text.secondary">Select category</Typography>;
                        const cats = [
                          { value: 'transactional', label: 'Transactional' },
                          { value: 'promotional', label: 'Promotional' },
                          { value: 'service', label: 'Service' },
                          { value: 'system', label: 'System' },
                        ];
                        return cats.find(c => c.value === val)?.label ?? val;
                      },
                    }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    <MenuItem value="transactional">Transactional</MenuItem>
                    <MenuItem value="promotional">Promotional</MenuItem>
                    <MenuItem value="service">Service</MenuItem>
                    <MenuItem value="system">System</MenuItem>
                  </TextField>
                  <Autocomplete
                    multiple
                    freeSolo
                    size="small"
                    options={[]}
                    value={currentTemplate?.keywords || []}
                    onChange={(_e: any, newValue: string[]) => setCurrentTemplate((prev: any) => prev ? { ...prev, keywords: newValue } : null)}
                    renderTags={(value: readonly string[], getTagProps) =>
                      value.map((option: string, index: number) => (
                        <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                      ))
                    }
                    renderInput={(params) => <TextField {...params} label="Keywords" placeholder="Type and press Enter" />}
                  />
                </Box>

                <TextField
                  label="Email Subject"
                  value={currentTemplate?.emailSubject || ''}
                  onChange={(e) => handleTextFieldChange('emailSubject', e.target.value)}
                  fullWidth
                  size="small"
                  required
                />
              </>
            )}

            {/* Available Variables Popover (always rendered so anchor works) */}
            <Popover
              open={variablesPopoverOpen}
              anchorEl={variablesAnchorEl}
              onClose={handleVariablesPopoverClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <Box sx={{ p: 2, maxWidth: 400, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Available Variables
                </Typography>
                {loadingColumns ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">
                      Loading columns...
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1
                  }}>
                    <Chip
                      key="consentURL"
                      label="{{consentURL}}"
                      size="small"
                      variant="outlined"
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        backgroundColor: (t) => t.palette.mode === 'dark' ? t.palette.grey[700] : t.palette.background.paper,
                        borderColor: (t) => t.palette.mode === 'dark' ? t.palette.grey[600] : t.palette.grey[400],
                        '&:hover': {
                          backgroundColor: (t) => t.palette.mode === 'dark' ? t.palette.grey[600] : t.palette.grey[200],
                        }
                      }}
                    />
                    {availableColumns.map((column) => (
                      <Chip
                        key={column}
                        label={`{{${column}}}`}
                        size="small"
                        variant="outlined"
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          backgroundColor: (t) => t.palette.mode === 'dark' ? t.palette.grey[700] : t.palette.background.paper,
                          borderColor: (t) => t.palette.mode === 'dark' ? t.palette.grey[600] : t.palette.grey[400],
                          '&:hover': {
                            backgroundColor: (t) => t.palette.mode === 'dark' ? t.palette.grey[600] : t.palette.grey[200],
                          }
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Popover>

            {/* EmailBuilder – on mobile, only show when Layout or Preview is active; pass mobileActivePanel */}
            {(!isMobile || mobileActiveSection === 'layout' || mobileActiveSection === 'preview') && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <EmailBuilder
                    value={currentTemplate?.emailBuilderConfig || undefined}
                    onChange={(value: EmailBuilderValue) => {
                      if (handleEmailBuilderChange) {
                        handleEmailBuilderChange(value);
                      }
                    }}
                    onExportHtml={(html: string) => {
                      handleTextFieldChange('templateContent', html);
                    }}
                    mobileActivePanel={isMobile ? (mobileActiveSection as 'layout' | 'preview') : undefined}
                  />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EmailTemplateDialog;
